import { Loader2, Plus, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Money } from "@workspace/accounting/components";
import { CheckOperation } from "@workspace/operations/check-operation";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { ScrollArea } from "@workspace/ui/components/scroll-area";

import { eposApi } from "../../api";
import type { TabOrderLine } from "../../types";
import type { EposTab } from "../epos-context";
import { useEposContext } from "../epos-context";
import { PrintTabButton } from "./print-tab-button";
import { TabOrderCompBadge } from "./tab-order-comp-badge";
import { TabPayments } from "./tab-payments";

/**
 * HA-554 — a tab's detail: each order it holds (its status, total and what it still owes) and the tab's figures, all
 * from the server. Tap an open order to work on it, **New round** starts another order on the tab, **Pay tab** (HA-554
 * Part 2) takes payments against what it owes, **Print Tab bill** (HA-360 Part B, staff-terminal-print-receipt) prints
 * every round on one bill, its payments can be refunded (HA-789), and **Close tab** (staff-terminal-manage-tabs) closes a tab with nothing outstanding. Closing waits for the server (D-HA-24): the tab
 * leaves the panel once the server's ActiveTabs no longer have it.
 */
export function TabDetailDialog({
	tab,
	onOpenChange,
	onPayTab,
}: {
	tab: EposTab | null;
	onOpenChange: (open: boolean) => void;
	/** HA-554 Part 2 — Pay Tab: take payments against the tab's outstanding balance. */
	onPayTab?: (tabId: string) => void;
}) {
	const {
		tenant,
		venueId,
		operations,
		createNewOrder,
		setActiveOrderId,
		setActiveTabId,
		setView,
	} = useEposContext();
	const queryClient = useQueryClient();

	const detailQuery = useQuery({
		...eposApi.getTabOptions(tenant, venueId, tab?.id ?? ""),
		enabled: !!tab,
	});

	const closeTab = useMutation({
		...eposApi.closeTabOptions(tenant, venueId),
		onSuccess() {
			toast.success("Tab closed");
			void queryClient.invalidateQueries({
				queryKey: [tenant, venueId, "epos"],
			});
			onOpenChange(false);
		},
		onError(error) {
			toast.error("Couldn't close the tab", {
				description: error instanceof Error ? error.message : "Unknown error",
			});
			void detailQuery.refetch();
		},
	});

	// HA-357 slice D (D7) — Remove / Add the tab's own service charge line; the server answers with the tab.
	const tabServiceCharge = useMutation({
		...eposApi.setTabServiceChargeOptions(tenant, venueId),
		onSuccess(updated, { action }) {
			queryClient.setQueryData(
				[tenant, venueId, "epos", "tabs", updated.tab.id],
				updated,
			);
			void queryClient.invalidateQueries({
				queryKey: [tenant, venueId, "epos"],
			});
			toast.success(
				action === "remove" ? "Service charge removed" : "Service charge added",
			);
		},
		onError(error) {
			toast.error("Couldn't change the service charge", {
				description: error instanceof Error ? error.message : "Unknown error",
			});
			void detailQuery.refetch();
		},
	});

	const detail = detailQuery.data;
	const serviceChargeLine = detail?.tab.serviceChargeLine?.active
		? detail.tab.serviceChargeLine
		: null;
	const outstanding = detail?.figures.outstanding ?? tab?.outstanding;
	const nothingOutstanding = (outstanding?.value ?? 1) === 0;

	const openOrder = (line: TabOrderLine) => {
		if (!tab || line.status !== "Open") return;
		setActiveTabId(tab.id);
		setActiveOrderId(line.id);
		setView("products");
		onOpenChange(false);
	};

	const newRound = async () => {
		if (!tab) return;
		const created = await createNewOrder({ tabId: tab.id });
		if (!created) return;
		setView("products");
		onOpenChange(false);
	};

	const serverName = (orderId: string) =>
		tab?.orders.find((o) => o.id === orderId)?.server.personalDetails
			.preferredName ?? null;

	return (
		<Dialog open={!!tab} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{tab ? tab.name || `#${tab.number}` : ""}</DialogTitle>
					<DialogDescription>
						Tab #{tab?.number} ·{" "}
						{detail?.orders.length ?? tab?.orders.length ?? 0} order
						{(detail?.orders.length ?? tab?.orders.length ?? 0) !== 1
							? "s"
							: ""}
					</DialogDescription>
				</DialogHeader>

				{detailQuery.isLoading ? (
					<div className="flex items-center justify-center py-8">
						<Loader2 className="size-6 animate-spin text-muted-foreground" />
					</div>
				) : (
					<>
						<ScrollArea className="max-h-[50vh]">
							<ul aria-label="Orders on this tab" className="space-y-2">
								{(detail?.orders ?? []).map((line, index) => {
									const name = serverName(line.id);
									return (
										<li key={line.id}>
											<button
												type="button"
												disabled={line.status !== "Open"}
												onClick={() => openOrder(line)}
												className="flex w-full items-center justify-between gap-2 rounded-md border px-3 py-3 text-left enabled:hover:bg-muted/50 disabled:cursor-default"
											>
												<span className="flex flex-col">
													<span className="font-medium">Round {index + 1}</span>
													{name && (
														<span className="text-xs text-muted-foreground">
															{name}
														</span>
													)}
												</span>
												<span className="flex items-center gap-2">
													<TabOrderCompBadge line={line} />
													<Badge
														variant={
															line.status === "Open" ? "default" : "secondary"
														}
													>
														{line.status}
													</Badge>
													<Money {...line.total} />
												</span>
											</button>
										</li>
									);
								})}
							</ul>
						</ScrollArea>

						{detail && (
							<dl className="grid grid-cols-2 gap-1 border-t pt-3 text-sm">
								{serviceChargeLine && (
									<>
										<dt className="text-muted-foreground">Service charge</dt>
										<dd
											className="flex items-center justify-end gap-2 font-medium"
											data-testid="tab-service-charge"
										>
											{serviceChargeLine.removedByStaff ? (
												<span className="text-muted-foreground">
													Removed (<Money {...serviceChargeLine.amount} />)
												</span>
											) : (
												<Money {...serviceChargeLine.amount} />
											)}
											<CheckOperation
												operation="staff-terminal-manage-tabs"
												operations={operations}
											>
												<Button
													size="sm"
													variant="outline"
													disabled={tabServiceCharge.isPending || !tab}
													onClick={() =>
														tab &&
														tabServiceCharge.mutate({
															tabId: tab.id,
															action: serviceChargeLine.removedByStaff
																? "add"
																: "remove",
														})
													}
												>
													{serviceChargeLine.removedByStaff
														? "Add service charge"
														: "Remove service charge"}
												</Button>
											</CheckOperation>
										</dd>
									</>
								)}
								<dt className="text-muted-foreground">Tab total</dt>
								<dd className="text-right font-medium">
									<Money {...detail.figures.total} />
								</dd>
								<dt className="text-muted-foreground">Outstanding</dt>
								<dd
									className="text-right font-medium"
									data-testid="tab-outstanding"
								>
									<Money {...detail.figures.outstanding} />
								</dd>
							</dl>
						)}
						{/* HA-789 — the tab's Pay Tab payments, each refundable. */}
						{detail && <TabPayments detail={detail} />}
					</>
				)}

				<DialogFooter className="gap-2 sm:justify-between">
					<CheckOperation
						operation="staff-terminal-manage-tabs"
						operations={operations}
					>
						{nothingOutstanding && (
							<Button
								variant="outline"
								disabled={closeTab.isPending || !tab}
								onClick={() => tab && closeTab.mutate({ tabId: tab.id })}
							>
								{closeTab.isPending && (
									<Loader2 className="size-4 animate-spin" />
								)}
								Close tab
							</Button>
						)}
					</CheckOperation>
					<div className="flex gap-2">
						{/* HA-360 Part B (AC9) — the tab's bill, every round on one document. */}
						{tab && (
							<CheckOperation
								operation="staff-terminal-print-receipt"
								operations={operations}
							>
								<PrintTabButton tabId={tab.id} label="Print Tab bill" />
							</CheckOperation>
						)}
						{!nothingOutstanding && onPayTab && (
							<Button
								variant="secondary"
								disabled={!tab}
								onClick={() => tab && onPayTab(tab.id)}
							>
								<Wallet className="size-4" />
								Pay tab
							</Button>
						)}
						<Button onClick={() => void newRound()} disabled={!tab}>
							<Plus className="size-4" />
							New round
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
